/**
 * Timesheet bundles, keyed by employee id.
 *
 * Each bundle has:
 *   - dailyRows: one row per workday in the period (for timesheet-range view)
 *   - blocks: TimelineBlock[] for a representative day (timesheet-daily view)
 *
 * The dailyRows cover the last 30 calendar days from FROZEN_NOW, skipping
 * weekends. Project assignments rotate through a few of each employee's
 * plausible projects; generated deterministically so snapshots stay stable.
 *
 * Not an attempt at realistic utilization — it's pre-baked presentation data.
 */

import type { DayType } from '@/types/entities';
import type { TimelineBlock } from '@/types/timeline';
import { FROZEN_NOW_MS } from '@/lib/frozenTime';
import { EMPLOYEES } from './employees';

export interface TimesheetDailyRow {
  date: string;
  dayType: DayType;
  totalHours: number;
  billableHours: number;
  clientId: string | null;
  projectId: string | null;
}

export interface TimesheetBundle {
  dailyRows: readonly TimesheetDailyRow[];
  blocks: readonly TimelineBlock[];
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Format a Date as YYYY-MM-DD (UTC). */
function ymd(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Day-type cadence — most days are full, some halves, a few check-ins. */
const DAY_TYPE_CADENCE: readonly DayType[] = [
  'full',
  'full',
  'full',
  'half',
  'full',
  'full',
  'full',
  'checkin',
  'full',
  'half',
];

/** Hours for each day type. */
const HOURS_BY_TYPE: Record<DayType, { total: number; billable: number }> = {
  full: { total: 8.0, billable: 6.5 },
  half: { total: 4.0, billable: 3.0 },
  checkin: { total: 1.5, billable: 0.5 },
};

/** Plausible project rotation per employee — mix of billable and general. */
const ROTATIONS: Record<string, readonly { clientId: string | null; projectId: string | null }[]> =
  {
    'emp-sarah-chen': [
      { clientId: 'cli-meridian-capital', projectId: 'prj-meridian-restructuring' },
      { clientId: 'cli-apex-holdings', projectId: 'prj-apex-acquisition' },
      { clientId: 'cli-pinnacle-financial', projectId: 'prj-pinnacle-regulatory' },
      { clientId: null, projectId: 'prj-firm-admin' },
    ],
    'emp-marcus-rivera': [
      { clientId: 'cli-harmon-industries', projectId: 'prj-harmon-litigation' },
      { clientId: 'cli-stellar-dynamics', projectId: 'prj-stellar-litigation' },
      { clientId: 'cli-community-legal', projectId: 'prj-community-probono' },
      { clientId: null, projectId: 'prj-general' },
    ],
    'emp-anita-patel': [
      { clientId: 'cli-meridian-capital', projectId: 'prj-meridian-mna' },
      { clientId: 'cli-apex-holdings', projectId: 'prj-apex-governance' },
      { clientId: 'cli-pacific-ventures', projectId: 'prj-pacific-vc' },
    ],
    'emp-james-obrien': [
      { clientId: 'cli-stellar-dynamics', projectId: 'prj-stellar-contract' },
      { clientId: 'cli-harmon-industries', projectId: 'prj-harmon-employment' },
      { clientId: null, projectId: 'prj-internal-training' },
    ],
    'emp-wei-zhang': [
      { clientId: 'cli-techventure-labs', projectId: 'prj-techventure-patents' },
      { clientId: 'cli-quantum-biotech', projectId: 'prj-quantum-ip-licensing' },
      { clientId: 'cli-techventure-labs', projectId: 'prj-techventure-ip-strategy' },
    ],
    'emp-eva-kowalski': [
      { clientId: 'cli-apex-holdings', projectId: 'prj-apex-compliance' },
      { clientId: 'cli-meridian-capital', projectId: 'prj-meridian-regulatory' },
      { clientId: 'cli-northwood-partners', projectId: 'prj-northwood-real-estate' },
      { clientId: null, projectId: 'prj-knowledge-mgmt' },
    ],
    'emp-daniel-kim': [
      { clientId: 'cli-anderson-trust', projectId: 'prj-anderson-estate' },
      { clientId: 'cli-crestview-holdings', projectId: 'prj-crestview-tax-planning' },
      { clientId: null, projectId: 'prj-firm-admin' },
    ],
    'emp-lisa-thompson': [
      { clientId: 'cli-anderson-trust', projectId: 'prj-anderson-wealth' },
      { clientId: 'cli-crestview-holdings', projectId: 'prj-crestview-succession' },
      { clientId: 'cli-pinnacle-financial', projectId: 'prj-pinnacle-tax' },
    ],
  };

/** A representative full-day timeline (09:00 → 17:30) shared by all employees. */
const SAMPLE_BLOCKS: readonly TimelineBlock[] = [
  {
    startMin: 9 * 60,
    endMin: 9 * 60 + 45,
    label: 'Outlook — Morning Email',
    colorToken: 'timeline-billable',
    app: 'outlook',
    detail: 'Client correspondence and calendar triage',
  },
  {
    startMin: 9 * 60 + 45,
    endMin: 11 * 60 + 30,
    label: 'Corporate Restructuring',
    colorToken: 'timeline-billable',
    app: 'word',
    detail: 'Drafting restructuring memo — Meridian Capital',
  },
  {
    startMin: 11 * 60 + 30,
    endMin: 12 * 60,
    label: 'Team Sync',
    colorToken: 'timeline-general',
    app: 'teams',
    detail: 'Weekly Corporate practice standup',
  },
  {
    startMin: 12 * 60,
    endMin: 13 * 60,
    label: 'Break',
    colorToken: 'timeline-idle',
  },
  {
    startMin: 13 * 60,
    endMin: 14 * 60 + 30,
    label: 'M&A Due Diligence',
    colorToken: 'timeline-billable',
    app: 'excel',
    detail: 'Financial review — Apex acquisition',
  },
  {
    startMin: 14 * 60 + 30,
    endMin: 15 * 60 + 15,
    label: 'Westlaw Research',
    colorToken: 'timeline-billable',
    app: 'chrome',
    detail: 'Regulatory precedent research',
  },
  {
    startMin: 15 * 60 + 15,
    endMin: 16 * 60 + 45,
    label: 'Regulatory Advisory',
    colorToken: 'timeline-billable',
    app: 'word',
    detail: 'Drafting regulatory memo — Meridian Capital',
  },
  {
    startMin: 16 * 60 + 45,
    endMin: 17 * 60 + 30,
    label: 'Firm Administration',
    colorToken: 'timeline-general',
    app: 'outlook',
    detail: 'Timesheet review and internal email',
  },
];

/** Build one bundle from the employee id + seed rotation. */
function buildBundle(empId: string): TimesheetBundle {
  const rotation = ROTATIONS[empId] ?? [{ clientId: null, projectId: 'prj-general' }];
  const rows: TimesheetDailyRow[] = [];

  // Walk back 30 days from FROZEN_NOW, skipping Sat/Sun.
  for (let i = 29; i >= 0; i--) {
    const date = new Date(FROZEN_NOW_MS - i * DAY_MS);
    const dow = date.getUTCDay();
    if (dow === 0 || dow === 6) continue;

    const dayType = DAY_TYPE_CADENCE[rows.length % DAY_TYPE_CADENCE.length] ?? 'full';
    const hours = HOURS_BY_TYPE[dayType];
    const rot = rotation[rows.length % rotation.length] ?? {
      clientId: null,
      projectId: 'prj-general',
    };
    const isNonBillableProject =
      rot.projectId === 'prj-general' ||
      rot.projectId === 'prj-firm-admin' ||
      rot.projectId === 'prj-internal-training' ||
      rot.projectId === 'prj-knowledge-mgmt';

    rows.push({
      date: ymd(date),
      dayType,
      totalHours: hours.total,
      billableHours: isNonBillableProject ? 0 : hours.billable,
      clientId: rot.clientId,
      projectId: rot.projectId,
    });
  }

  return { dailyRows: rows, blocks: SAMPLE_BLOCKS };
}

export const TIMESHEETS: Record<string, TimesheetBundle> = Object.fromEntries(
  EMPLOYEES.map((e) => [e.id, buildBundle(e.id)]),
);
